// frontend/src/api/config.ts
// Central configuration for API base URLs
// Extend Window interface to include ENV variables if injected
declare global {
  interface Window {
    ENV?: {
      VITE_WORKER_API_URL?: string;
      VITE_FASTAPI_DEV_URL?: string; // For FastAPI dev
      VITE_FASTAPI_PROD_URL?: string; // For FastAPI prod
    };
  }
}

// Static mapping for Vite environment variables (required for build-time replacement)
// Vite requires static access, not dynamic key access, for proper build-time replacement
const VITE_ENV_VARS = {
  VITE_STRIPE_PUBLISHABLE_KEY: import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY,
  VITE_AWS_API_URL: import.meta.env.VITE_AWS_API_URL,
  VITE_ENV: import.meta.env.VITE_ENV,
  VITE_WORKER_API_URL: import.meta.env.VITE_WORKER_API_URL,
  VITE_FASTAPI_DEV_URL: import.meta.env.VITE_FASTAPI_DEV_URL,
  VITE_FASTAPI_PROD_URL: import.meta.env.VITE_FASTAPI_PROD_URL,
  // Auth/verification flags
  VITE_REQUIRE_EMAIL_VERIFICATION: import.meta.env.VITE_REQUIRE_EMAIL_VERIFICATION,
  // Feature flags
  VITE_USE_WS_CHAT: import.meta.env.VITE_USE_WS_CHAT,
  VITE_USE_SSE_CHAT: import.meta.env.VITE_USE_SSE_CHAT,
  VITE_STREAM_FALLBACK: import.meta.env.VITE_STREAM_FALLBACK,
  // PostHog Analytics Environment Variables
  VITE_PUBLIC_POSTHOG_KEY: import.meta.env.VITE_PUBLIC_POSTHOG_KEY,
  VITE_PUBLIC_POSTHOG_HOST: import.meta.env.VITE_PUBLIC_POSTHOG_HOST,
} as const;

// Safe env accessor that works in both Vite and CRA/Webpack
export function getEnvVar(key: string, fallback?: string): string | undefined {
  // 1) Vite style: Use static mapping (required for build-time replacement)
  try {
    if (typeof import.meta !== 'undefined' && import.meta.env) {
      // Use the static mapping for VITE_ prefixed variables
      const viteVal = VITE_ENV_VARS[key as keyof typeof VITE_ENV_VARS];
      if (typeof viteVal === 'string' && viteVal.length > 0) return viteVal;
    }
  } catch {}

  // 2) Window-injected ENV (optional)
  try {
    const winVal = (typeof window !== 'undefined' && (window as any)?.ENV?.[key]) || undefined;
    if (typeof winVal === 'string' && winVal.length > 0) return winVal;
  } catch {}

  // 3) CRA style: process.env.REACT_APP_* (and process.env.KEY fallback)
  try {
    const procEnv = (typeof process !== 'undefined' && (process as any)?.env) || undefined;
    const direct = procEnv?.[key];
    const reactAppKey = key.startsWith('VITE_') ? `REACT_APP_${key.slice(5)}` : `REACT_APP_${key}`;
    const reactApp = procEnv?.[reactAppKey];
    const val = direct || reactApp;
    if (typeof val === 'string' && val.length > 0) return val;
  } catch {}

  return fallback;
}

// Determine environment (default to development)
// Support 'local' as an alias for 'development'
// In test environments, force localhost URLs for MSW interception
const rawEnv = getEnvVar('VITE_ENV', 'development') || 'development';
const isTestEnvironment = (
  (typeof process !== 'undefined' && process.env?.NODE_ENV === 'test') ||
  (typeof import.meta !== 'undefined' && import.meta.env?.MODE === 'test') ||
  (typeof global !== 'undefined' && (global as any).VITEST) ||
  (typeof window !== 'undefined' && (window as any).__VITEST__) ||
  getEnvVar('VITE_TEST_MODE') === 'true'
);
const env = (rawEnv === 'local' ? 'development' : rawEnv) as 'development' | 'production';

// Define URLs for different environments
// In test environment, use localhost URLs for MSW interception
const workerApiUrls = {
  development: isTestEnvironment 
    ? "http://localhost:8787"  // Use localhost in tests for MSW
    : (getEnvVar('VITE_WORKER_API_URL') || "https://worker-api-dev.jerriven.workers.dev"),
  production:
    getEnvVar('VITE_WORKER_API_URL') || "https://worker-api.jerriven.workers.dev",
};

const fastapiAppUrls = {
  development: isTestEnvironment 
    ? "http://localhost:8000"  // Use localhost in tests for MSW
    : (getEnvVar('VITE_FASTAPI_DEV_URL') || "https://dylo3pt88c.execute-api.us-west-2.amazonaws.com"), // AWS API Gateway dev URL
  production:
    getEnvVar('VITE_FASTAPI_PROD_URL') || "https://bjuh55yl8e.execute-api.us-west-2.amazonaws.com", // AWS API Gateway prod URL
};

// Export the correct URL based on the environment
export const WORKER_API_URL: string = workerApiUrls[env];
export const FASTAPI_APP_BASE_URL: string = fastapiAppUrls[env]; // This will be just the base, /api/v1 will be appended in services
export const API_BASE_URL: string = WORKER_API_URL; // For document service compatibility
export const API_V1_BASE_URL: string = `${WORKER_API_URL}/api/v1`;

// Base URL for the AWS Lambda API - use same as FastAPI for email verification
export const AWS_API_URL: string =
  getEnvVar('VITE_AWS_API_URL') || FASTAPI_APP_BASE_URL;

// Log the URLs being used (optional, for debugging)
if (process.env.NODE_ENV !== 'production') {
  console.log(`Environment: ${rawEnv} (mapped to: ${env})`);
  console.log(`Is Test Environment: ${isTestEnvironment}`);
  console.log(`Worker API URL: ${WORKER_API_URL}`);
  console.log(`FastAPI App Base URL: ${FASTAPI_APP_BASE_URL}`);
  console.log(`AWS API URL: ${AWS_API_URL}`);
}
